<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ECLIPSE_EDGE_OFFSET, ECLIPSE_TRANSIT_SECONDS, titleFor, titleTint } from '@shared/titles';
import { createSnowField, drawSnow, stepSnow } from './title-snow';
import { createMeteorField, drawMeteors, stepMeteors } from './title-meteors';

/**
 * A player's title, drawn the same way everywhere it appears.
 *
 * One component for four surfaces — the nameplate over a tank, the scoreboard,
 * the kill feed and the shop card — because the whole product is *the thing you
 * bought is the thing other people see*. Two implementations would be two
 * chances for the card to promise something the plate does not deliver, and the
 * card is the one the money changes hands on.
 *
 * It renders **nothing at all** for a missing, cleared or unrecognised id.
 * `titleFor` is total, so a title withdrawn in a future build degrades to a
 * plain nameplate rather than to a broken glyph or the literal `title_nightjar`.
 *
 * ── Why this is not an image ─────────────────────────────────────────────────
 * The rank insignia exist as art and were the obvious thing to reuse, and they
 * cannot be: the finding they were shipped with is that they need 64–80 px to be
 * countable, and the title-bar rank chip already had to stay textual at ~32 px
 * because "you can see there are chevrons but not how many". A nameplate line is
 * about 9 px tall — a third of the size that was already too small. Nothing with
 * internal detail survives that, so nothing here has any: the ladder is built
 * out of **contrast, framing and one solid blob**, all of which are exactly as
 * legible at 9 px as at 90, and all of which are drawn in CSS at
 * `devicePixelRatio` rather than fetched as a raster that would be soft on half
 * the displays that run this. Same argument `RadarScope` makes for its bezel.
 */
const props = withDefaults(
  defineProps<{
    titleId?: string;
    /**
     * How much room this surface has.
     *
     * - **`plate`** — over a tank and in the scoreboard. The full treatment, at
     *   the smallest size anything in this game is drawn at.
     * - **`feed`** — the kill feed. The word and its colour, and *never* the
     *   rule or the chip: a feed line is already a bordered box, and a chip
     *   inside it is a box inside a box. What survives there is the colour,
     *   which is the part that says whose line this is at a glance.
     * - **`card`** — the shop. The same treatment as `plate`, three times the
     *   size, so a buyer can read what they are getting.
     */
    variant?: 'plate' | 'feed' | 'card';
    /**
     * How fast a slow animation runs here. The shop passes 8 so a buyer can watch
     * Eclipse's 11-minute transit in under a minute and a half, and labels it;
     * the arena leaves it at 1.
     */
    speed?: number;
  }>(),
  { variant: 'plate', speed: 1 },
);

const profile = computed(() => titleFor(props.titleId));

/**
 * `chip` is dropped on the feed, so the treatment ladder collapses to colour
 * there. Stated here rather than in three template conditions.
 */
const style = computed(() => (props.variant === 'feed' ? 'plain' : profile.value?.style ?? 'plain'));
const marked = computed(() => props.variant !== 'feed' && profile.value?.mark === true);

const vars = computed(() => {
  const colour = profile.value?.colour ?? '#C8C8C8';
  return {
    '--title-colour': colour,
    // Both derived from the one hex through `titleTint`, so the shop's swatch
    // and the battlefield's plate cannot come to different conclusions about
    // what "tinted" means.
    '--title-fill': titleTint(colour, 0.16),
    '--title-edge': titleTint(colour, 0.5),
    '--eclipse-duration': `${ECLIPSE_TRANSIT_SECONDS / Math.max(1, props.speed)}s`,
    '--eclipse-offset': `${(ECLIPSE_EDGE_OFFSET * 100).toFixed(2)}%`,
  };
});

/**
 * The emblem. Drawn as a CSS mask over `currentColor`, so one white-on-alpha
 * PNG per title takes whatever colour the title has — and so the shop card and
 * the battlefield plate cannot disagree about which emblem a word carries. It
 * replaces the hairlines and the corner-diamond the three treatments used to
 * wear, which said "ornament" without ever saying *which* title. Not on the
 * feed, for the reason the chip is not: a feed line is already a box.
 */
const iconUrl = computed(() =>
  props.variant !== 'feed' && profile.value?.icon ? `url("${profile.value.icon}")` : null,
);

/** A realistic badge, when the title has one, replaces the mask. */
const artUrl = computed(() => (props.variant !== 'feed' ? profile.value?.art ?? null : null));

/** Both sides, the right flipped — see `TitleProfile.mirror`. */
const mirrored = computed(() => !!iconUrl.value && profile.value?.mirror === true);

/**
 * The eclipse emblem is drawn, not masked: a sun disc in the title colour with a
 * pulsing corona, and a dark moon clipped to it that crosses once per battle.
 * One sun, after the word — not the mirrored pair — so the word sits left and
 * the sun can be large enough to read the moon on.
 */
const eclipse = computed(() => !!iconUrl.value && profile.value?.animated === 'eclipse');

/** Snow over the whole badge. Not on the feed, which is colour only. */
const snowing = computed(() => props.variant !== 'feed' && profile.value?.animated === 'snowstorm');

/** Shooting stars over the badge. Not on the feed, for the same reason. */
const meteors = computed(() => props.variant !== 'feed' && profile.value?.animated === 'starfall');

/** The left copy is flipped only when the emblem itself looks left. */
const leftFlipped = computed(() => profile.value?.faces === 'left');

// ─── The glitch ──────────────────────────────────────────────────────────────

/**
 * An animated title pixelates at random moments — never more than five seconds
 * apart, never less than about a second — by redrawing the word on a canvas at a
 * handful of falling resolutions with a couple of torn rows and a one-pixel
 * colour split, then handing back to the real text.
 *
 * Presentation only, and entirely local: no timer here touches the simulation,
 * the wire or another component, so a hundred plates on a hundred tanks cannot
 * disagree about anything that matters. The real text is what is in the DOM the
 * whole time (only hidden for ~350 ms), so a screen reader and a screenshot
 * between bursts see the word. Off for `prefers-reduced-motion`, and a missing
 * canvas (jsdom, a locked-down browser) simply never glitches.
 */
const GLITCH_MIN_GAP_MS = 1200;
const GLITCH_MAX_GAP_MS = 4400;
const GLITCH_FRAME_MS = 55;
const GLITCH_PIXEL_STEPS = [7, 3, 5, 2, 4, 1];

const wordEl = ref<HTMLElement | null>(null);
const canvasEl = ref<HTMLCanvasElement | null>(null);
const glitching = ref(false);
let gapTimer: ReturnType<typeof setTimeout> | null = null;
let frameTimer: ReturnType<typeof setTimeout> | null = null;

function reducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

function drawGlitchFrame(pixel: number, torn: boolean): boolean {
  const canvas = canvasEl.value;
  const word = wordEl.value;
  const ctx = canvas?.getContext('2d');
  if (!canvas || !word || !ctx) return false;
  const rect = word.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return false;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const pad = Math.ceil(rect.height * 0.35);
  const w = Math.ceil(rect.width) + pad * 2;
  const h = Math.ceil(rect.height) + pad * 2;
  if (canvas.width !== Math.ceil(w * dpr) || canvas.height !== Math.ceil(h * dpr)) {
    canvas.width = Math.ceil(w * dpr);
    canvas.height = Math.ceil(h * dpr);
  }
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  canvas.style.left = `${-pad}px`;
  canvas.style.top = `${-pad}px`;

  const cs = getComputedStyle(word);
  const font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  const colour = profile.value?.colour ?? '#C8C8C8';
  const text = profile.value?.text.toUpperCase() ?? '';
  const spacing = parseFloat(cs.letterSpacing) || 0;

  // Draw the word once at 1/pixel resolution, then blow it up with smoothing off.
  const small = document.createElement('canvas');
  small.width = Math.max(1, Math.ceil((w * dpr) / pixel));
  small.height = Math.max(1, Math.ceil((h * dpr) / pixel));
  const sctx = small.getContext('2d');
  if (!sctx) return false;
  sctx.setTransform(dpr / pixel, 0, 0, dpr / pixel, 0, 0);
  sctx.font = font;
  sctx.fillStyle = colour;
  sctx.textBaseline = 'middle';
  if ('letterSpacing' in sctx) (sctx as unknown as { letterSpacing: string }).letterSpacing = `${spacing}px`;
  sctx.fillText(text, pad, h / 2);

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingEnabled = false;
  if (torn && pixel > 1) {
    // A one-pixel colour split under the word.
    ctx.globalAlpha = 0.55;
    ctx.globalCompositeOperation = 'lighter';
    ctx.drawImage(small, -dpr * 2, 0, canvas.width, canvas.height);
    ctx.drawImage(small, dpr * 2, 0, canvas.width, canvas.height);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }
  ctx.drawImage(small, 0, 0, canvas.width, canvas.height);
  if (torn) {
    // Tear two rows sideways.
    for (let i = 0; i < 2; i += 1) {
      const sy = Math.floor(Math.random() * canvas.height * 0.8);
      const sh = Math.max(2, Math.floor(canvas.height * (0.08 + Math.random() * 0.12)));
      const dx = Math.round((Math.random() - 0.5) * 10 * dpr);
      const slice = ctx.getImageData(0, sy, canvas.width, sh);
      ctx.clearRect(0, sy, canvas.width, sh);
      ctx.putImageData(slice, dx, sy);
    }
  }
  return true;
}

function runBurst(): void {
  let step = 0;
  const next = (): void => {
    if (step >= GLITCH_PIXEL_STEPS.length) {
      glitching.value = false;
      scheduleGlitch();
      return;
    }
    const pixel = GLITCH_PIXEL_STEPS[step];
    if (!drawGlitchFrame(pixel, pixel > 1 && step % 2 === 0)) {
      glitching.value = false;
      scheduleGlitch();
      return;
    }
    glitching.value = true;
    step += 1;
    frameTimer = setTimeout(next, GLITCH_FRAME_MS);
  };
  next();
}

function scheduleGlitch(): void {
  if (gapTimer) clearTimeout(gapTimer);
  if (profile.value?.animated !== 'glitch' || reducedMotion()) return;
  const gap = GLITCH_MIN_GAP_MS + Math.random() * (GLITCH_MAX_GAP_MS - GLITCH_MIN_GAP_MS);
  gapTimer = setTimeout(runBurst, gap);
}

function stopGlitch(): void {
  if (gapTimer) clearTimeout(gapTimer);
  if (frameTimer) clearTimeout(frameTimer);
  gapTimer = frameTimer = null;
  glitching.value = false;
}

// ─── Canvas weather: the snowstorm and the shooting stars ───────────────────

/**
 * A canvas over the badge, stepped and drawn once a frame by a pure module —
 * `title-snow.ts` for Summit, `title-meteors.ts` for Starfall. Sized from the
 * badge and resized with it; both fields are measured in em, so a resize only
 * rebuilds one when the badge's shape actually changes. The meteor canvas
 * reaches `METEOR_HEADROOM_EM` above the badge so stars cross over the top of
 * the word instead of being clipped to the chip.
 */
const METEOR_HEADROOM_EM = 0.8;

interface Weather {
  w: number;
  h: number;
}

const rootEl = ref<HTMLElement | null>(null);
const snowEl = ref<HTMLCanvasElement | null>(null);
const meteorEl = ref<HTMLCanvasElement | null>(null);
let weatherFrame: number | null = null;
let weatherResize: ResizeObserver | null = null;

function runWeather<F extends Weather>(
  canvas: HTMLCanvasElement,
  headroomEm: number,
  create: (w: number, h: number) => F,
  step: (field: F, dt: number) => void,
  draw: (ctx: CanvasRenderingContext2D, field: F, em: number) => void,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  let field: F | null = null;
  let em = 0;
  const fit = (): void => {
    const root = rootEl.value;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    const font = parseFloat(getComputedStyle(root).fontSize) || 10;
    if (rect.width < 2 || rect.height < 2) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const h = rect.height + headroomEm * font;
    canvas.width = Math.ceil(rect.width * dpr);
    canvas.height = Math.ceil(h * dpr);
    em = font * dpr;
    const wEm = rect.width / font;
    const hEm = h / font;
    if (!field || Math.abs(field.w - wEm) > 0.05 || Math.abs(field.h - hEm) > 0.05) {
      field = create(wEm, hEm);
    }
  };
  // A badge mounted inside a hidden shelf measures zero; the observer fits it
  // the moment it is laid out, and the loop draws nothing until then.
  fit();
  if (typeof ResizeObserver !== 'undefined' && rootEl.value) {
    weatherResize = new ResizeObserver(fit);
    weatherResize.observe(rootEl.value);
  }
  let last = performance.now();
  const frame = (now: number): void => {
    if (field) {
      step(field, (now - last) / 1000);
      draw(ctx, field, em);
    }
    last = now;
    weatherFrame = requestAnimationFrame(frame);
  };
  weatherFrame = requestAnimationFrame(frame);
}

function startWeather(): void {
  stopWeather();
  if (reducedMotion() || typeof requestAnimationFrame === 'undefined') return;
  if (snowing.value && snowEl.value) {
    runWeather(snowEl.value, 0, (w, h) => createSnowField(w, h), stepSnow, (ctx, field, em) => {
      ctx.fillStyle = '#ffffff';
      drawSnow(ctx, field, em);
    });
  } else if (meteors.value && meteorEl.value) {
    const colour = profile.value?.colour ?? '#ffffff';
    runWeather(
      meteorEl.value,
      METEOR_HEADROOM_EM,
      (w, h) => createMeteorField(w, h),
      stepMeteors,
      (ctx, field, em) => drawMeteors(ctx, field, em, colour),
    );
  }
}

function stopWeather(): void {
  if (weatherFrame !== null) cancelAnimationFrame(weatherFrame);
  weatherFrame = null;
  weatherResize?.disconnect();
  weatherResize = null;
}

onMounted(() => {
  scheduleGlitch();
  startWeather();
});
onBeforeUnmount(() => {
  stopGlitch();
  stopWeather();
});
watch(
  () => profile.value?.id,
  () => {
    stopGlitch();
    scheduleGlitch();
  },
);
watch([() => profile.value?.id, () => props.variant], startWeather, { flush: 'post' });
</script>

<template>
  <span
    v-if="profile"
    ref="rootEl"
    class="title-badge"
    :class="[`v-${variant}`, `s-${style}`, { marked: marked && !iconUrl, iconed: !!iconUrl, animated: profile.animated, glitching, snowing, 'has-meteors': meteors, neon: profile.animated === 'neon' && variant !== 'feed' }]"
    :style="vars"
    :data-title="profile.id"
  >
    <img v-if="artUrl" class="title-art" :class="{ flip: leftFlipped }" :src="artUrl" alt="" aria-hidden="true" />
    <i
      v-else-if="iconUrl && !eclipse"
      class="title-icon"
      :class="{ flip: leftFlipped }"
      aria-hidden="true"
      :style="{ '--title-icon': iconUrl }"
    ></i>
    <i v-else-if="marked" class="title-mark" aria-hidden="true"></i>
    <i v-if="!iconUrl && style === 'rule'" class="title-rule" aria-hidden="true"></i>
    <span ref="wordEl" class="title-word">
      {{ profile.text }}
      <canvas v-if="profile.animated === 'glitch'" ref="canvasEl" class="title-glitch" aria-hidden="true"></canvas>
    </span>
    <i v-if="!iconUrl && style === 'rule'" class="title-rule" aria-hidden="true"></i>
    <template v-if="mirrored">
      <img v-if="artUrl" class="title-art" :class="{ flip: !leftFlipped }" :src="artUrl" alt="" aria-hidden="true" />
      <i
        v-else
        class="title-icon"
        :class="{ flip: !leftFlipped }"
        aria-hidden="true"
        :style="{ '--title-icon': iconUrl ?? 'none' }"
      ></i>
    </template>
    <i v-if="eclipse" class="title-eclipse" aria-hidden="true"><i class="eclipse-moon"></i></i>
    <i v-if="!iconUrl && marked" class="title-mark" aria-hidden="true"></i>
    <canvas v-if="snowing" ref="snowEl" class="title-snow" aria-hidden="true"></canvas>
    <canvas v-if="meteors" ref="meteorEl" class="title-meteors" aria-hidden="true"></canvas>
  </span>
</template>

<style scoped>
.title-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.32em;
  color: var(--title-colour);
  /*
   * Uppercase and tracked, which is a deliberate trade rather than a style.
   * At this size mixed case is marginally easier to *read*, but a title is not
   * read — it is recognised, the way a unit patch is. Tracked caps make a short
   * word into a horizontal band that separates cleanly from the nickname on the
   * line below, which is the thing that actually has to happen at 60 m.
   */
  text-transform: uppercase;
  letter-spacing: 0.16em;
  font-weight: 600;
  white-space: nowrap;
  line-height: 1;
}

/*
 * The word carries the shadow, not the box: a shadow under a translucent chip
 * darkens the chip rather than lifting the letters off the battlefield.
 *
 * The negative right margin absorbs the trailing letter-space that tracking
 * adds after the final character. Without it the badge is half a space wider on
 * the right than on the left, which on a plate that is *centred over a tank*
 * reads as the whole thing sitting slightly off the vehicle.
 */
.title-word {
  margin-right: -0.16em;
  text-shadow:
    0 1px 3px rgba(0, 0, 0, 0.95),
    0 0 6px rgba(0, 0, 0, 0.6);
}

/*
 * ── Why every class here is prefixed ────────────────────────────────────────
 *
 * The root was `.title` and the parts were `.word`, `.rule` and `.mark`, and
 * that was a real bug rather than a style preference. A Vue `scoped` stylesheet
 * stamps its `data-v-…` attribute onto a **child component's root node** as
 * well as its own elements, so `ItemCard`'s own `.title { flex-direction:
 * column }` — the header block holding a card's name and tier — matched this
 * component's root and stood the whole badge on end: the two hairlines of the
 * `rule` treatment stacked above and below the word instead of sitting either
 * side of it, on every card in the shop, while the arena's plate was fine.
 *
 * It photographs as a design choice, which is what makes it worth a paragraph.
 * A shared component's root class has to be one nobody else would pick.
 */

/* ─── Sizes ──────────────────────────────────────────────────────────────── */

.v-plate {
  font-size: 0.56rem;
}

.v-feed {
  font-size: 0.62rem;
  letter-spacing: 0.1em;
}

.v-card {
  /*
   * 0.82rem, not the 0.95 it started at. The grid is `minmax(220px, 1fr)` and
   * the longest word on the shelf is `QUARTERMASTER` — thirteen tracked capitals,
   * which at 0.95rem ran to the edge of a narrow card and out of a chipped one.
   * A shop card that clips the product is worse than a smaller one.
   */
  font-size: 0.82rem;
  letter-spacing: 0.18em;
}

/* ─── Treatments ─────────────────────────────────────────────────────────── */

/*
 * A hairline either side. Six pixels of rule at plate size, which is less than
 * two letters wide and is still unmistakable, because the eye reads *a word with
 * something on both ends* long before it reads the something.
 */
.title-rule {
  width: 0.7em;
  height: 1px;
  background: currentColor;
  opacity: 0.55;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
}

.s-chip {
  padding: 0.18em 0.45em;
  border: 1px solid var(--title-edge);
  border-radius: 2px;
  background: var(--title-fill);
  /* Sits over grass, buildings and sky, so the plate needs its own floor rather
     than relying on whatever is behind it. */
  box-shadow:
    inset 0 0 0 1px rgba(0, 0, 0, 0.35),
    0 1px 3px rgba(0, 0, 0, 0.7);
}

/*
 * The mark: a filled square on its corner.
 *
 * Not a glyph. A `◆` would be a font lookup with a tofu fallback and would carry
 * whatever weight and vertical metrics that font happened to give it; this is
 * three pixels of colour that renders identically everywhere. It is the only
 * ornament in the set and it is reserved for the diamond shelf, which is what
 * makes it worth anything.
 */
.title-mark {
  width: 0.34em;
  height: 0.34em;
  flex: none;
  background: currentColor;
  transform: rotate(45deg);
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.8);
}

/* The emblem — a mask over the title colour. 1.3em reads at nameplate size. */
.title-icon {
  width: 1.3em;
  height: 1.3em;
  flex: none;
  background: currentColor;
  -webkit-mask: var(--title-icon) center / contain no-repeat;
  mask: var(--title-icon) center / contain no-repeat;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.85));
}

.title-icon.flip,
.title-art.flip {
  transform: scaleX(-1);
}

/* A realistic badge: its own plate and colours, so it is a picture, not a mask. */
.title-art {
  width: 1.9em;
  height: 1.9em;
  flex: none;
  border-radius: 0.22em;
  object-fit: cover;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.55),
    0 1px 3px rgba(0, 0, 0, 0.8);
}

/* The glitch canvas sits over the word and is only visible mid-burst; the real
   text stays in the DOM, merely hidden, so layout never moves. */
.title-word {
  position: relative;
}

.title-glitch {
  position: absolute;
  pointer-events: none;
  opacity: 0;
}

.glitching .title-glitch {
  opacity: 1;
}

.glitching .title-word {
  color: transparent;
  text-shadow: none;
}

.glitching .title-glitch {
  color: initial;
}

/* ─── Snowstorm ──────────────────────────────────────────────────────────── */

.snowing {
  position: relative;
}

/* The flakes are drawn by `title-snow.ts`; this only lays the canvas over the
   whole badge, word and emblems alike. */
.title-snow {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  border-radius: inherit;
}

/* ─── Eclipse ────────────────────────────────────────────────────────────── */

/*
 * The sun is the box itself, so its corona is its own shadow and is never
 * clipped; the moon is a child the sun clips to its disc, so nothing of it shows
 * outside the sun — just as with the real thing. Larger than an emblem, and let
 * out of the chip vertically by a negative margin so the chip does not grow to
 * hold it.
 */
.title-eclipse {
  position: relative;
  width: 1.75em;
  height: 1.75em;
  margin: -0.45em -0.1em -0.45em 0.15em;
  flex: none;
  border-radius: 50%;
  overflow: hidden;
  background: radial-gradient(circle, #fff 0 28%, currentColor 72%);
  /* Its own light, not the word's: the word goes near black at totality, and
     the corona is exactly what must keep shining then. */
  animation:
    eclipse-glow 3.2s ease-in-out infinite alternate,
    eclipse-sun var(--eclipse-duration) linear infinite alternate;
}

@keyframes eclipse-sun {
  0% { color: #ffb45e; }
  50% { color: #cdb4ff; }
  100% { color: #ffb45e; }
}

.eclipse-moon {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: #12101a;
  box-shadow: inset 0 0 0.1em rgba(255, 255, 255, 0.25);
  transform: translateX(calc(-1 * var(--eclipse-offset)));
  /* Linear, one crossing per battle, then back again rather than snapping. */
  animation: eclipse-transit var(--eclipse-duration) linear infinite alternate;
}

@keyframes eclipse-transit {
  from { transform: translateX(calc(-1 * var(--eclipse-offset))); }
  to { transform: translateX(var(--eclipse-offset)); }
}

@keyframes eclipse-glow {
  from {
    box-shadow:
      0 0 0.16em 0.03em currentColor,
      0 0 0.4em 0.08em var(--title-edge);
  }
  to {
    box-shadow:
      0 0 0.3em 0.1em currentColor,
      0 0 0.9em 0.26em currentColor;
  }
}

/*
 * The light changes with the transit: golden-hour orange while the sun is a
 * third covered, dusk violet at totality, and back to orange as the moon moves
 * off. One keyframe set on the same clock and direction as the moon, so the
 * colour and the coverage can never drift apart. The word, the chip, its edge
 * and the sun itself all follow, because each of them is drawn from `color`.
 */
.title-badge[data-title='title_eclipse'] {
  animation: eclipse-light var(--eclipse-duration) linear infinite alternate;
}

.title-badge[data-title='title_eclipse'].s-chip {
  animation-name: eclipse-light, eclipse-chip;
}

@keyframes eclipse-light {
  0% { color: #ffb45e; }
  50% { color: #1a1326; }
  100% { color: #ffb45e; }
}

@keyframes eclipse-chip {
  0% {
    background-color: rgba(255, 180, 94, 0.18);
    border-color: rgba(255, 180, 94, 0.55);
  }
  50% {
    background-color: rgba(12, 8, 20, 0.9);
    border-color: rgba(70, 50, 110, 0.55);
  }
  100% {
    background-color: rgba(255, 180, 94, 0.18);
    border-color: rgba(255, 180, 94, 0.55);
  }
}

/*
 * The word is lit from the sun's side, so its shadow falls to the left only: a
 * dark umber while the sun is out, turning to a pale violet glow at totality,
 * when the corona is the only light left. On the same clock as the colour. At
 * totality the word and its plate are both near black on purpose: the letters
 * are barely there, and the violet rim on their left is what draws them.
 */
.title-badge[data-title='title_eclipse'] .title-word {
  animation: eclipse-word var(--eclipse-duration) linear infinite alternate;
}

@keyframes eclipse-word {
  0% {
    text-shadow:
      -0.09em 0.02em 0.1em rgba(70, 34, 8, 0.9),
      0 1px 2px rgba(0, 0, 0, 0.6);
  }
  50% {
    text-shadow:
      -0.07em 0 0.05em rgba(214, 192, 255, 0.95),
      -0.1em 0.02em 0.22em rgba(184, 150, 255, 0.6);
  }
  100% {
    text-shadow:
      -0.09em 0.02em 0.1em rgba(70, 34, 8, 0.9),
      0 1px 2px rgba(0, 0, 0, 0.6);
  }
}

/* ─── Neon ───────────────────────────────────────────────────────────────── */

/*
 * Lit tubing: a near-white core with the colour as its glow, on a dark plate so
 * the glow has something to light. The glow hums slowly; the word alone
 * flickers twice a cycle, unevenly, like one tube on its way out — the emblem
 * stays steady, which is what makes the flicker read as a fault rather than a
 * blink.
 */
.neon.s-chip {
  background-color: rgba(26, 6, 22, 0.82);
  border-color: var(--title-colour);
  box-shadow:
    0 0 0.25em var(--title-colour),
    inset 0 0 0.3em var(--title-edge),
    0 1px 3px rgba(0, 0, 0, 0.7);
  animation: neon-hum 2.6s ease-in-out infinite alternate;
}

.neon .title-word {
  color: #ffe6f6;
  text-shadow:
    0 0 0.06em #fff,
    0 0 0.18em var(--title-colour),
    0 0 0.45em var(--title-colour),
    0 0 0.8em var(--title-edge);
  animation: neon-flicker 7.3s linear infinite;
}

.neon .title-icon {
  background: #ffe6f6;
  filter: drop-shadow(0 0 0.12em var(--title-colour)) drop-shadow(0 0 0.3em var(--title-colour));
}

@keyframes neon-hum {
  from {
    box-shadow:
      0 0 0.18em var(--title-colour),
      inset 0 0 0.22em var(--title-edge),
      0 1px 3px rgba(0, 0, 0, 0.7);
  }
  to {
    box-shadow:
      0 0 0.45em var(--title-colour),
      inset 0 0 0.4em var(--title-edge),
      0 1px 3px rgba(0, 0, 0, 0.7);
  }
}

/* Steps, not easing: a tube is on or it is not. */
@keyframes neon-flicker {
  0%, 61%, 61.6%, 62.4%, 63.4%, 88.6%, 89.4%, 100% { opacity: 1; }
  61.3% { opacity: 0.25; }
  62% { opacity: 0.85; }
  62.8% { opacity: 0.15; }
  89% { opacity: 0.4; }
}

/* ─── Starfall ───────────────────────────────────────────────────────────── */

/* The stars are drawn by `title-meteors.ts`; this lays the canvas over the
   badge and `METEOR_HEADROOM_EM` (0.8em) above it, so they cross the top of
   the word rather than being clipped to the chip. */
.has-meteors {
  position: relative;
}

.title-meteors {
  position: absolute;
  left: 0;
  top: -0.8em;
  width: 100%;
  height: calc(100% + 0.8em);
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .neon.s-chip,
  .neon .title-word,
  .title-badge[data-title='title_eclipse'] .title-word,
  .title-eclipse,
  .eclipse-moon,
  .title-badge[data-title='title_eclipse'] {
    animation: none;
  }

  .title-meteors {
    display: none;
  }
}
</style>
